import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import type { AttendanceState, PublicUserDTO, StampKind } from '@wf/shared'
import { ApiError } from '../api/client'
import { saveCachedAttendance, useAttendanceMe, useStamp } from '../attendance/useAttendance'
import { stampErrorText } from '../attendance/stampError'
import { formatHMS } from '../time/format'
import { ACCENT_OPTIONS, getAccent, setAccent, subscribeAccent } from './accent'
import { useLogout } from './auth'
import { initials } from './initials'
import { getThemePick, setTheme, subscribeTheme, type ThemePick } from './theme'

/** Profil-Kontrollzentrum der Kopfleiste: Avatar (mit Anwesenheits-Ring) + Popover in fünf Sektionen —
 *  Identität, Zeiterfassung, Konto & Sicherheit, Darstellung, Abmelden. Bündelt, was vorher lose in der
 *  Kopfleiste lag (Stempeluhr, Theme-Pillen), damit oben nur noch zwei ruhige Signale stehen: der
 *  Verbindungs-Punkt und der Avatar, dessen Ring den Stempel-Stand zeigt (grün = an, gelb = Pause).
 *
 *  A11y: bewusst ein Disclosure-Pattern (Button mit aria-expanded), KEIN ARIA-Menü — das Popover enthält
 *  echte Bedienelemente (Stempel-Knöpfe, Umschalter, Farbwahl), kein reines Menü (Begründung wie NavMenu). */
export function ProfileMenu({ user }: { user: PublicUserDTO }) {
  const [open, setOpen] = useState(false)
  const logout = useLogout()
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const { data: attendance } = useAttendanceMe()

  // Klick außerhalb schließt das Menü (Pointerdown, damit es vor dem Klick auf ein anderes Bedienelement
  // greift); Escape ebenso.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Letzten Anwesenheitsstand spiegeln (Sofort-Render beim Boot, Offline) — lebte vorher in der
  // Header-Stempeluhr und muss dauerhaft gemountet bleiben, deshalb hier und nicht in der Sektion.
  useEffect(() => {
    if (attendance) saveCachedAttendance(attendance)
  }, [attendance])
  useEffect(() => {
    const persist = (): void => {
      if (attendance) saveCachedAttendance(attendance)
    }
    window.addEventListener('pagehide', persist)
    document.addEventListener('visibilitychange', persist)
    return () => {
      window.removeEventListener('pagehide', persist)
      document.removeEventListener('visibilitychange', persist)
    }
  }, [attendance])

  const hasClock = !user.isSuperadmin && !!attendance // Super-Admin hat keine Stempeluhr (wie Alt-App)
  const ringClass = hasClock
    ? attendance.status === 'an'
      ? ' wf-avatar--an'
      : attendance.status === 'pause'
        ? ' wf-avatar--pause'
        : ''
    : ''

  const go = (to: string): void => {
    setOpen(false)
    navigate(to)
  }

  return (
    <div className="wf-profile" ref={ref}>
      <button
        type="button"
        className={`wf-avatar${ringClass}`}
        aria-expanded={open}
        aria-label={`Konto: ${user.name}`}
        onClick={() => setOpen((v) => !v)}
      >
        {initials(user.name)}
      </button>
      {open ? (
        <div className="wf-pmenu wf-pmenu--center" aria-label="Profil">
          {/* A · Identität */}
          <div className="wf-pmenu__sec wf-pmenu__who">
            <span className="wf-avatar wf-avatar--plain" aria-hidden="true">
              {initials(user.name)}
            </span>
            <div className="wf-pmenu__whotext">
              <span className="wf-pmenu__name">{user.name}</span>
              <span className="wf-pmenu__role">{user.position || user.role}</span>
            </div>
          </div>

          {/* B · Zeiterfassung */}
          {hasClock ? <AttendanceSection state={attendance} menuOpen={open} /> : null}

          {/* C · Konto & Sicherheit */}
          <div className="wf-pmenu__sec">
            <button type="button" className="wf-pmenu__row" onClick={() => go('/account')}>
              <IconUser />
              <span className="wf-pmenu__rowlabel">Mein Konto</span>
              <IconChevron />
            </button>
            <button type="button" className="wf-pmenu__row" onClick={() => go('/account')}>
              <IconShield />
              <span className="wf-pmenu__rowlabel">Zwei-Faktor-Schutz</span>
              <span className={user.totpEnabled ? 'wf-pmenu__chip wf-pmenu__chip--ok' : 'wf-pmenu__chip wf-pmenu__chip--warn'}>
                {user.totpEnabled ? 'aktiv' : 'einrichten'}
              </span>
            </button>
          </div>

          {/* D · Darstellung */}
          <div className="wf-pmenu__sec">
            <p className="wf-pmenu__seclabel">Darstellung</p>
            <ThemeSegment />
            <p className="wf-pmenu__seclabel">Akzentfarbe</p>
            <AccentSwatches />
          </div>

          {/* E · Abmelden */}
          <div className="wf-pmenu__sec">
            <button
              type="button"
              className="wf-pmenu__row wf-pmenu__row--danger"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              <IconLogout />
              <span className="wf-pmenu__rowlabel">Abmelden</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** Stempeluhr-Sektion: Statuszeile (mit Live-Zähler aus dem SERVER-persistierten liveSince — überlebt
 *  Reload/Standby, nie eine RAM-Uhr) + passende Stempel-Knöpfe. Der Sekunden-Tick läuft nur bei offenem
 *  Menü und offenem Arbeitssegment — die Kopfleiste selbst ist damit komplett bewegungsfrei. */
function AttendanceSection({ state, menuOpen }: { state: AttendanceState; menuOpen: boolean }) {
  const stamp = useStamp()
  const [now, setNow] = useState(() => Date.now())
  const { status, version, todaySeconds, liveSince } = state

  useEffect(() => {
    if (!menuOpen || liveSince == null) return
    const handle = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(handle)
  }, [menuOpen, liveSince])

  const elapsed = liveSince != null ? todaySeconds + Math.max(0, Math.floor((now - liveSince) / 1000)) : todaySeconds
  const click = (kind: StampKind): void => stamp.mutate({ kind, version })
  const busy = stamp.isPending
  const conflict = stamp.error instanceof ApiError && stamp.error.status === 409

  const mainLabel =
    status === 'an' ? `Eingestempelt · läuft seit ${hhmm(liveSince ?? now)}` : status === 'pause' ? 'Pause' : 'Ausgestempelt'

  return (
    <div className="wf-pmenu__sec" aria-label="Stempeluhr">
      <p className="wf-pmenu__seclabel">Zeiterfassung</p>
      <div className={`wf-pmenu__att wf-pmenu__att--${status}`}>
        <span className="wf-pmenu__attdot" aria-hidden="true" />
        <div>
          <span className="wf-pmenu__attmain">{mainLabel}</span>
          <span className="wf-pmenu__attsub" role={status === 'an' ? 'timer' : undefined} aria-label="Arbeitszeit heute">
            {formatHMS(elapsed)} heute
          </span>
        </div>
      </div>
      <div className="wf-pmenu__attbtns">
        {status === 'an' && (
          <>
            <button type="button" className="wf-btn wf-btn--sm wf-btn--ghost" onClick={() => click('pause_start')} disabled={busy}>
              Pause
            </button>
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => click('gehen')} disabled={busy}>
              Gehen
            </button>
          </>
        )}
        {status === 'pause' && (
          <>
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => click('pause_ende')} disabled={busy}>
              Weiter
            </button>
            <button type="button" className="wf-btn wf-btn--sm wf-btn--ghost" onClick={() => click('gehen')} disabled={busy}>
              Gehen
            </button>
          </>
        )}
        {status === 'aus' && (
          <button type="button" className="wf-btn wf-btn--sm" onClick={() => click('kommen')} disabled={busy}>
            Kommen
          </button>
        )}
      </div>
      {conflict && (
        <span className="wf-pmenu__atthint" role="status">
          {stampErrorText(stamp.error)}
        </span>
      )}
    </div>
  )
}

/** Dunkel / Hell / System als Segment-Schalter. „System" folgt dem Gerät (matchMedia, live). */
function ThemeSegment() {
  const pick = useSyncExternalStore(subscribeTheme, getThemePick, getThemePick)
  const options: ReadonlyArray<{ id: ThemePick; label: string }> = [
    { id: 'dark', label: 'Dunkel' },
    { id: 'light', label: 'Hell' },
    { id: 'system', label: 'System' },
  ]
  return (
    <div className="wf-pmenu__seg" role="group" aria-label="Farbschema">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={pick === option.id ? 'wf-pmenu__segbtn wf-pmenu__segbtn--active' : 'wf-pmenu__segbtn'}
          aria-pressed={pick === option.id}
          onClick={() => setTheme(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/** Akzentfarbe als Farbpunkte — die persönliche Note, gespeichert am Gerät (accent.ts). */
function AccentSwatches() {
  const accent = useSyncExternalStore(subscribeAccent, getAccent, getAccent)
  return (
    <div className="wf-swatches" role="group" aria-label="Akzentfarbe">
      {ACCENT_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`wf-swatch wf-swatch--${option.id}${accent === option.id ? ' wf-swatch--active' : ''}`}
          aria-pressed={accent === option.id}
          aria-label={option.label}
          onClick={() => setAccent(option.id)}
        />
      ))}
    </div>
  )
}

function hhmm(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/* Icons: Lucide-Pfade, inline kopiert (harte CSP — keine Icon-CDNs/Pakete; Konvention wie InfoTip). */
function IconUser() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </svg>
  )
}
function IconShield() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}
function IconLogout() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </svg>
  )
}
function IconChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  )
}
